import React, { useState, useEffect } from 'react';
import { message, notification } from 'antd';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';

import { ADD_PROPOSAL_ATTACHMENTS } from 'graphql/mutations/proposalMutation';
import { PROPOSAL_SUBSCRIPTION_TOPIC } from 'constants/index';
import UploadContentLibraryItem from 'components/UploadContentLibraryItem/UploadContentLibraryItem';
import FileCopier from 'components/FileCopier';

/**
 * AttachmentModal
 * @param {Object} visible - The visible state
 * @param {Function} showAttachmentModal - The function to show the attachment modal
 * @param {Object} prop - The proposal
 * @param {Function} setProp - The function to set the proposal
 * @param {Object} propRef - The proposal reference
 * @param {boolean} isTemplate - Whether the proposal is a template
 */
const AttachmentModal = ({ visible, showAttachmentModal, prop, setProp, propRef, isTemplate }) => {
  const [copy] = FileCopier();
  const [showPropModal, setShowPropModal] = useState(visible);

  useEffect(() => {
    setShowPropModal(visible);
  }, [visible]);

  /**
   * Add the attachments to the proposal
   * @returns {Promise} - Promise that resolves when the attachment is complete
   */
  const [addAttachment] = useMutation(ADD_PROPOSAL_ATTACHMENTS, {
    onCompleted: ({ addProposalAttachment }) => {
      propRef.current = { ...prop, ...addProposalAttachment };
      setProp({ ...prop, ...addProposalAttachment });
    },
  });

  /**
   * Handle the attachment of files
   * @param {Array} results - The results of the files
   * @returns {Promise} - Promise that resolves when the attachment is complete
   */
  const handleAttachFiles = async (results) => {
    if (!results || results.length === 0) {
      return;
    }

    try {
      const attachments = await Promise.all(
        results.map(async (result) => {
          // Check if this is from library (has src property) or uploaded (has url)
          const sourceUrl = result.src || result.url;

          // If it's from library, copy it to proposal folder
          if (result.src) {
            const url = await copy({
              fromUrl: sourceUrl,
              prefix: `props/${prop._id}`,
            });
            return {
              url,
              type: result.type || 'file',
              name: result.name,
              size: result.size || 0,
            };
          }
          // Already uploaded, use as-is
          return {
            url: result.url,
            type: result.type || 'file',
            name: result.name || result.filename,
            size: result.size || 0,
          };
        })
      );

      await addAttachment({
        variables: {
          proposalId: prop._id,
          attachments,
          topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${prop?.channel || prop?.auid || prop?.uid}`,
          type: isTemplate ? 'template' : 'proposal',
        },
      });

      const editorWrapper = document.querySelector(
        '.draft-body .editor .editor-layout .editor-edit-section .editor-wrapper'
      );

      if (editorWrapper) {
        editorWrapper.scrollTop = editorWrapper.scrollHeight;
      }

      notification.success({
        description: 'Your files has been attached to the proposal.',
      });
      showAttachmentModal(false);
    } catch (error) {
      let errorMessage = 'Failed to attach files. Please try again.';
      if (error?.graphQLErrors?.[0]?.message) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error?.networkError?.result?.errors?.[0]?.message) {
        errorMessage = error.networkError.result.errors[0].message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      message.error(errorMessage);
    }
  };

  /**
   * Handle the cancellation of the attachment modal
   * @returns {void}
   */
  const handleCancel = () => {
    setShowPropModal(false);
    showAttachmentModal(false);
  };

  return (
    <UploadContentLibraryItem
      showUploadModal={showPropModal}
      cancelUploadModal={handleCancel}
      type="attachments"
      onSuccess={handleAttachFiles}
      openUploadModal={() => setShowPropModal(true)}
      multiSelect={true}
      selectedFolderKey={null}
      prop={prop}
    />
  );
};

AttachmentModal.propTypes = {
  prop: PropTypes.instanceOf(Object).isRequired,
  visible: PropTypes.bool.isRequired,
  showAttachmentModal: PropTypes.func.isRequired,
  setProp: PropTypes.func.isRequired,
  isTemplate: PropTypes.bool,
};

export default AttachmentModal;
